import { z } from 'zod';
import { isSupportedLocale, localePath, type SupportedLocale } from './locale.js';

const editorPaths = [
  '/', '/map-radius-tool/', '/draw-circle-on-map/', '/map-marker/', '/mapping-tool/',
  '/world-map/', '/interactive-world-map/', '/us-map/', '/interactive-us-map/',
  '/africa-map/', '/asia-map/', '/europe-map/', '/canada-map/', '/australia-map/', '/uk-map/',
  '/distance-between-two-addresses/', '/route-planner/', '/service-area-widget/create/',
] as const;

export const savedMapEditorSchema = z.object({
  version: z.literal(1),
  path: z.enum(editorPaths),
  region: z.string().regex(/^[A-Za-z0-9_-]{1,100}$/).optional(),
  legend: z.array(z.object({ color: z.string().regex(/^#[0-9a-fA-F]{6}$/), label: z.string().max(48) })).max(100).optional(),
  opacity: z.number().min(0).max(1).optional(),
  borderWidth: z.number().min(0).max(20).optional(),
});
export type SavedMapEditor = z.infer<typeof savedMapEditorSchema>;

export function mapEditorForPathname(pathname: string): SavedMapEditor {
  const parts = pathname.split('/').filter(Boolean);
  if (isSupportedLocale(parts[0])) parts.shift();
  const path = parts.length ? `/${parts.join('/')}/` : '/';
  const parsed = savedMapEditorSchema.safeParse({ version: 1, path });
  return parsed.success ? parsed.data : { version: 1, path: '/' };
}

/** Only known editor routes and task parameters can enter a saved-map redirect. */
export function savedMapEditorUrl(editor: SavedMapEditor, mapId: string, locale: SupportedLocale, source?: URL): string {
  const path = editor.path === '/service-area-widget/create/' ? editor.path : localePath(editor.path, locale);
  const params = new URLSearchParams({ map: mapId });
  if (editor.region) params.set('region', editor.region);
  for (const key of ['open', 'share_map', 'share_section'] as const) {
    const value = source?.searchParams.get(key);
    if (value) params.set(key, value);
  }
  return `${path}?${params}`;
}
